import React, { useContext } from 'react';

import cx from 'classnames';

import { FormattedMessage } from 'react-intl';
import { TextButton, type TextButtonProps } from '@xingternal/button';
import { IconArrowRight, IconExternalLink } from '@xingternal/icons';

import { BodyCopy, Headline } from '@xingternal/typography';
import ExternalEditInfoBanner from '../ExternalEditInfoBanner';

import EditButton from '../EditButton';
import { EditContext } from '../../context';
import { useMainPageLoadModule } from '../../hooks';

import { SkeletonBody } from '@xingternal/skeleton';

import styles from './ModuleHeader.css';

export type ShowMoreTypes = TextButtonProps & {
  target?: string;
  copyKey?: string;
  number?: number;
  isLoading?: boolean;
  testId?: string;
  externalLink?: boolean;
};

type ModuleHeaderProps = {
  headlineCopyKey?: string;
  showMore?: ShowMoreTypes | null;
  editTestId?: string;
  CustomEditButton?: React.ReactNode;
  CustomButtons?: React.ReactNode[];
  externalEditInfo?: any;
  editTo?: string | null;
  children?: React.ReactNode;
  small?: boolean;
  className?: string;
};

const ModuleHeaderShowMore = ({
  showMore,
  hasMultipleCustomButtons,
}: {
  showMore: ShowMoreTypes;
  hasMultipleCustomButtons?: boolean;
}) => {
  const { copyKey, number, testId, externalLink, isLoading, ...buttonProps } =
    showMore;

  return (
    <span
      className={cx(styles.showMoreWrapper, {
        [styles.showMoreWrapperWithCustomBtns]: hasMultipleCustomButtons,
      })}
      data-testid={showMore.testId}
      data-cy={showMore.testId}
    >
      <TextButton
        className={cx(styles.showMore, showMore.className)}
        icon={showMore.externalLink ? IconExternalLink : IconArrowRight}
        iconAfter={true}
        loading={isLoading}
        {...buttonProps}
      >
        <BodyCopy
          fontWeight="bold"
          lineHeight="dense"
          size="small"
          noMargin
          as={'strong'}
        >
          <FormattedMessage
            id={showMore.copyKey}
            values={{ number: showMore.number }}
          />
        </BodyCopy>
      </TextButton>
    </span>
  );
};

const ModuleHeader = ({
  headlineCopyKey,
  showMore,
  children,
  small,
  editTo,
  editTestId,
  externalEditInfo,
  CustomEditButton,
  CustomButtons,
  className,
}: ModuleHeaderProps) => {
  useMainPageLoadModule();
  const { isEditing } = useContext(EditContext);
  const showCustomEditButton = isEditing && Boolean(CustomEditButton);
  const showCustomButtons = CustomButtons && CustomButtons.length > 0;
  const showMultipleCustomButtons = CustomButtons && CustomButtons.length > 1;

  return (
    <>
      <div
        className={cx(
          styles.header,
          { [styles.smallHeader]: small },
          { [styles.headerWithCustomBtns]: showMultipleCustomButtons },
          className
        )}
      >
        <div className={styles.headerHeadlineContainer}>
          <Headline
            size={'xxxlarge'}
            noMargin
            data-testid="module-header-title"
            className={cx(
              styles.headline,
              { [styles.headLineSmall]: small },
              { [styles.headlineWithCustomBtns]: showMultipleCustomButtons }
            )}
          >
            <FormattedMessage
              id={headlineCopyKey}
              defaultMessage={headlineCopyKey}
            />
          </Headline>
          {isEditing && editTo && (
            <EditButton
              size={'small'}
              showIcon={true}
              className={styles.editIcon}
              data-cy={editTestId}
              data-testid={editTestId}
              to={editTo}
            />
          )}
        </div>
        {showMore && showMore.isLoading && (
          <span className={styles.skeleton}>
            <SkeletonBody variant="primary" amountRows={1} />
          </span>
        )}
        <div
          className={cx(styles.buttonWrapper, {
            [styles.buttonWrapperWithCustomBtns]: showMultipleCustomButtons,
          })}
        >
          {(showCustomEditButton || showCustomButtons) && (
            <div className={styles.customButtonsWrapper}>
              {showCustomEditButton && CustomEditButton && (
                <div className={styles.customButton}>{CustomEditButton}</div>
              )}
              {CustomButtons &&
                CustomButtons.map((button, i) => (
                  <div key={i} className={styles.customButton}>
                    {button}
                  </div>
                ))}
            </div>
          )}
          {showMore && !showMore.isLoading && showMore.copyKey && (
            <ModuleHeaderShowMore
              showMore={showMore}
              hasMultipleCustomButtons={showMultipleCustomButtons}
            />
          )}
        </div>
        {children && <div className={styles.showMore}>{children}</div>}
      </div>
      {externalEditInfo && <ExternalEditInfoBanner {...externalEditInfo} />}
    </>
  );
};

export default ModuleHeader;
